/**
 * useLicense — React hook for Qlipper license state
 *
 * As of v2.2.1, license state lives on the PC backend. This hook just queries
 * the PC's /api/license/status endpoint. LAN clients (phone) get the same
 * answer as the PC frontend — license is bound to the Qlipper server instance.
 */

import { useState, useEffect, useCallback } from 'react';
import * as licenseService from '../services/licenseService.js';

export function useLicense() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [licenseInfo, setLicenseInfo] = useState(null);
    const [userEmail, setUserEmail] = useState(null);
    const [loading, setLoading] = useState(true); // start loading until server status fetched
    const [error, setError] = useState(null);

    // Fetch server-side status on mount — source of truth
    const refreshNow = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const status = await licenseService.refreshStatus();
            setIsAuthenticated(!!status.active);
            setIsVerified(!!status.active); // server having a stored license means it passed activation once
            setUserEmail(status.email || null);
            setLicenseInfo(status);
        } catch (err) {
            setIsAuthenticated(false);
            setIsVerified(false);
            setError('Cannot reach Qlipper server. Is the app running?');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshNow();
    }, [refreshNow]);

    // Activate with email + key — calls PC backend proxy
    const activateWithKey = useCallback(async (email, key) => {
        setLoading(true);
        setError(null);
        try {
            const result = await licenseService.activateWithKey(email, key);
            setIsAuthenticated(true);
            setIsVerified(result.valid);
            setUserEmail(email);
            setLicenseInfo(result.info);
            return result;
        } catch (err) {
            setIsAuthenticated(false);
            setIsVerified(false);
            const reason = err.message || 'Activation failed.';
            setError(reason);
            throw new Error(reason);
        } finally {
            setLoading(false);
        }
    }, []);

    // Verify current license (refreshes server-side cache + our status)
    const verifyNow = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await licenseService.verifyLicense();
            if (result.valid) {
                setIsVerified(true);
                setLicenseInfo(result.info);
            } else {
                setIsVerified(false);
                setError(result.reason || 'License verification failed.');
            }
        } catch (err) {
            setIsVerified(false);
            setError(err.message || 'License check failed.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Deactivate + clear server-side license
    const deactivateLicense = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await licenseService.deactivateLicense();
            setIsAuthenticated(false);
            setIsVerified(false);
            setLicenseInfo(null);
            setUserEmail(null);
        } catch (err) {
            setError(err.message || 'Deactivation failed.');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Logout = deactivate
    const logout = useCallback(async () => {
        await licenseService.logout();
        setIsAuthenticated(false);
        setIsVerified(false);
        setLicenseInfo(null);
        setUserEmail(null);
        setError(null);
    }, []);

    return {
        // State
        isAuthenticated,
        isVerified,
        licenseInfo,
        userEmail,
        loading,
        error,
        // Methods
        activateWithKey,
        verifyNow,
        deactivateLicense,
        logout,
        refreshNow,
        // Legacy — kept for backward compat, no-op
        login: async () => { throw new Error('login() removed — use activateWithKey'); },
        register: async () => { throw new Error('register() removed — use activateWithKey'); },
        activateLicense: activateWithKey,
    };
}

export default useLicense;
